import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Tooltip,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import If from '@components/if/if.tsx';
import { ICONS } from '@components/icons/icon.tsx';
import {
  DesktopIcon,
  InputSearchIcon
} from '@shared/components/icons/svg-icon.tsx';
import { AddDevicesMenuProps } from '@modules/digital-signage/model/group-settings.model.ts';
import {
  filterDevicesByQuery,
  toggleDeviceOption
} from '@modules/digital-signage/components/group-settings/utils/group-settings.utils.ts';
import DeviceGroupChip from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-devices-list/components/add-devices-menu/components/device-group-chip/device-group-chip.tsx';

const AddDevicesMenu: React.FC<AddDevicesMenuProps> = ({
  devices,
  selectedDevices,
  disabled,
  onDevicesChange
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState<string>('');
  const isOpen = !!anchor;

  const shownDevices = filterDevicesByQuery(devices, query);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setQuery('');
    setAnchor(event.currentTarget);
  };

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        onClick={openMenu}
        disabled={disabled}
        startIcon={ICONS.plusIcon}
        className={`shrink-0 min-w-25! h-8.5! px-3! disabled:opacity-50! [&_.MuiButton-startIcon_path]:stroke-current! ${
          isOpen
            ? 'border-(--primary)! bg-(--primary-100)! text-(--primary)!'
            : 'border-(--gray-6)! bg-(--gray-11)! hover:bg-(--gray-10)! text-(--dark-1)!'
        }`}
      >
        {t('add')}
      </Button>
      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={() => setAnchor(null)}
        disableAutoFocusItem
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { className: 'w-82 mt-1.5' },
          list: {
            className: 'py-0!',
            onKeyDown: (event: React.KeyboardEvent) => event.stopPropagation()
          }
        }}
      >
        <div
          className="p-2 border-b border-(--gray-9)"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <OutlinedInput
            fullWidth
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(
              'placeholder_digital_signage_group_add_devices_search'
            )}
            // eslint-disable-next-line no-restricted-syntax -- input text size, an input cannot use Typography
            className="h-9.5! rounded-[10px]! [&_.MuiOutlinedInput-notchedOutline]:rounded-[10px]! [&:not(:hover):not(.Mui-focused)_.MuiOutlinedInput-notchedOutline]:border-(--gray-9)! [&_.MuiInputBase-input]:py-2! [&_.MuiInputBase-input]:pr-2.5! [&_.MuiInputBase-input]:pl-0! [&_.MuiInputBase-input]:text-[13px]!"
            startAdornment={
              <InputAdornment position="start">
                <InputSearchIcon fill="var(--gray-2)" className="w-4! h-4!" />
              </InputAdornment>
            }
          />
        </div>
        <If
          condition={shownDevices.length > 0}
          emptyContent={
            <Typography
              variant="caption"
              component="p"
              className="px-4 py-5 text-center text-(--gray-2)!"
            >
              {t('no_results_found')}
            </Typography>
          }
        >
          <div className="max-h-90 overflow-y-auto p-1.5">
            {shownDevices.map((device) => {
              const isSelected = selectedDevices.some(
                (option) => option.id === device._id
              );
              const groupName = device.deviceGroup?.name;
              const toggleDevice = () =>
                onDevicesChange(toggleDeviceOption(selectedDevices, device));

              return (
                <MenuItem
                  key={device._id}
                  onClick={toggleDevice}
                  className={`[&:hover_.MuiCheckbox-root_path]:stroke-none! ${
                    isSelected ? 'bg-(--primary-50)!' : ''
                  }`}
                >
                  <span className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-(--gray-11) text-(--gray-2)">
                    <DesktopIcon
                      viewBox="0 0 39 39"
                      stroke="currentColor"
                      className="w-4! h-4!"
                    />
                  </span>
                  <div className="flex flex-col flex-1 min-w-0">
                    <Tooltip title={device.name} placement="top">
                      <Typography
                        variant="body2"
                        noWrap
                        className="text-[13px]! font-semibold! text-(--dark-1)!"
                      >
                        {device.name}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      noWrap
                      className="text-[11px]! text-(--gray-2)!"
                    >
                      {device.deviceId}
                    </Typography>
                  </div>
                  {groupName && <DeviceGroupChip name={groupName} />}
                  <Checkbox
                    className="primary-checkbox shrink-0"
                    checked={isSelected}
                    onClick={(event) => event.stopPropagation()}
                    onChange={toggleDevice}
                  />
                </MenuItem>
              );
            })}
          </div>
        </If>
      </Menu>
    </>
  );
};

export default AddDevicesMenu;
